import React, { useEffect, useState } from 'react';
import { AppShell } from '@/components/nav/AppShell';
import { estimatesApi, customersApi } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Plus, Send, Check, X, FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { fmtMoney, fmtDate, fmtDocNumber } from '@/lib/format';
import { EmptyState } from '@/components/empty/EmptyState';
import { ListSkeleton } from '@/components/common/ListSkeleton';

export default function EstimatesPage() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: '', line_items: [{ label: '', qty: 1, unit_price: 0, taxable: true }], notes: '' });

  const load = async () => {
    try {
      const [es, cs] = await Promise.all([estimatesApi.list(), customersApi.list()]);
      setItems(es);
      setCustomers(cs);
    } catch {
      // surfaced by the api response interceptor
    } finally {
      setLoaded(true);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await estimatesApi.create({ ...form, line_items: form.line_items.filter(li => li.label) });
      toast.success('Estimate created');
      setOpen(false);
      setForm({ customer_id: '', line_items: [{ label: '', qty: 1, unit_price: 0, taxable: true }], notes: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const send = async (id) => { await estimatesApi.send(id); toast.success('Estimate sent'); load(); };
  const approve = async (id) => { const r = await estimatesApi.approve(id); toast.success(`Approved → Job created ${r.job_id.slice(0, 6)}`); load(); };

  return (
    <AppShell title="Estimates">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between [&>*:last-child]:w-full sm:[&>*:last-child]:w-auto mb-6">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold">Estimates</h1>
          <p className="text-sm text-muted-foreground">Quote customers before scheduling.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />New estimate</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>New estimate</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Customer *</Label>
                <select
                  className="w-full h-10 sm:h-9 rounded-md border border-input bg-background px-3 text-sm shadow-xs transition-colors hover:border-border-strong"
                  value={form.customer_id}
                  onChange={e => setForm({ ...form, customer_id: e.target.value })}
                  required
                >
                  <option value="">Select customer</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Line items</Label>
                {form.line_items.map((li, i) => (
                  <div key={i} className="grid grid-cols-2 sm:grid-cols-12 gap-2">
                    <Input className="col-span-2 sm:col-span-6" placeholder="Item" value={li.label} onChange={e => { const arr = [...form.line_items]; arr[i].label = e.target.value; setForm({ ...form, line_items: arr }); }} />
                    <Input className="col-span-1 sm:col-span-2" type="number" step="0.01" value={li.qty} onChange={e => { const arr = [...form.line_items]; arr[i].qty = parseFloat(e.target.value) || 0; setForm({ ...form, line_items: arr }); }} />
                    <Input className="col-span-1 sm:col-span-3" type="number" step="0.01" value={li.unit_price} onChange={e => { const arr = [...form.line_items]; arr[i].unit_price = parseFloat(e.target.value) || 0; setForm({ ...form, line_items: arr }); }} />
                    <Button type="button" size="icon" variant="ghost" className="col-span-1 justify-self-end sm:justify-self-auto" onClick={() => setForm({ ...form, line_items: form.line_items.filter((_, idx) => idx !== i) })}><X className="h-4 w-4" /></Button>
                  </div>
                ))}
                <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, line_items: [...form.line_items, { label: '', qty: 1, unit_price: 0, taxable: true }] })}><Plus className="h-3 w-3 mr-1" />Add item</Button>
              </div>
              <DialogFooter><Button type="submit">Create</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {!loaded ? (
        <ListSkeleton />
      ) : items.length === 0 ? (
        <EmptyState icon={FileText} title="No estimates yet" description="Send an estimate to your customer to convert it into a job." />
      ) : (
        <Card><CardContent className="p-0">
          <div className="divide-y">
            {items.map(e => {
              const c = customers.find(x => x.id === e.customer_id);
              return (
                <div key={e.id} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between px-4 py-3 transition-colors hover:bg-muted/40">
                  <div>
                    <div className="font-medium text-sm">{fmtDocNumber(e, 'Estimate')} · {c?.name || '—'}</div>
                    <div className="text-xs text-muted-foreground">{fmtDate(e.created_at)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{e.status}</Badge>
                    <div className="font-semibold tabular-nums w-20 text-right">{fmtMoney(e.total)}</div>
                    {e.status === 'draft' && <Button size="sm" variant="outline" onClick={() => send(e.id)}><Send className="h-3 w-3 mr-1" />Send</Button>}
                    {e.status === 'sent' && <Button size="sm" onClick={() => approve(e.id)}><Check className="h-3 w-3 mr-1" />Approve</Button>}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent></Card>
      )}
    </AppShell>
  );
}
